import React, { useEffect, useRef, useState } from "react";
import { Button, DialogActions, DialogContent, CircularProgress, Box, FormControl, Select, MenuItem, Typography, DialogTitle } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCameraTrack } from "../../hooks/useCameraTrack";
import "./CameraCapture.css";
import permissionVideo from "../../assets/permissionVideo.mp4";

const CameraCapture = ({ onCapture, onClose }) => {
  const { t } = useTranslation();

  const [isVideoReady, setIsVideoReady] = useState(false);
  const videoRef = useRef(null);

  const [selectedCamera, setSelectedCamera] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const videoDevicesRef = useRef([]);
  const { track: rawCameraTrack, error: cameraTrackError } = useCameraTrack(selectedCamera,true);
  async function fetchDevices() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      setPermissionDenied(false);
      stream.getTracks().forEach(track => track.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(d => d.kind === "videoinput");
      videoDevicesRef.current = videoInputs;
      if (videoDevicesRef.current.length > 0) setSelectedCamera(videoDevicesRef.current[0]);
    } catch (err) {
      console.error("Permission Error:", err);
      setPermissionDenied(true);
      setIsVideoReady(false);
      setSelectedCamera(null);
    }
  }
  useEffect(() => {
    fetchDevices();
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      if (permissionDenied) {
        fetchDevices();
      }
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [permissionDenied]);
  useEffect(() => {
    let permissionStatus;

  async function listenForPermissionChange() {
    try {
      permissionStatus = await navigator.permissions.query({ name: "camera" });      permissionStatus.onchange = () => {
        if (permissionStatus.state === "granted") {
          setPermissionDenied(false);
          fetchDevices();   // Re-run camera detection
        }
        else{
          setPermissionDenied(true);
          setSelectedCamera(null);
        }

        
      };
    } catch (err) {
      console.warn("Permissions API not supported");
    }
  }

  listenForPermissionChange();

  return () => {
    if (permissionStatus) permissionStatus.onchange = null;
  };
  }, []);
  const handleCapture = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const capturedImage = canvas.toDataURL("image/jpeg");
    onCapture(capturedImage);
  }
  useEffect(() => {
    if (cameraTrackError) {
      console.error("Camera Hook Error:", cameraTrackError);
      setPermissionDenied(true);
      setIsVideoReady(false);
    }
  }, [cameraTrackError]);
  useEffect(() => {      
    if (rawCameraTrack && videoRef.current) {
      const stream = new MediaStream([rawCameraTrack]);
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(e => console.error(e));
    }
  }, [rawCameraTrack]);
  return (
    <>
      {permissionDenied ? (
        <>
        <DialogTitle>
            <div className="permission-type-text"
            style={{ display: "flex", alignItems: "flex-start" }}
            >
            <span
                    className="material-symbols-outlined"
                    style={{ fontSize: '24px', color: '#d32f2f', marginBottom: '10px' }}
                >
                    videocam_off
                </span>

                <Typography>{t("cam_permission_text")}</Typography>
            </div>
        </DialogTitle>
        <DialogContent>
            <Box>
            <video
            src={permissionVideo}
            autoPlay 
            loop 
            muted 
            playsInline
            style={{ width: "100%", borderRadius: "16px" }}
          />
            </Box>
        </DialogContent>
        </>
      ) : (
        <>
          <DialogContent>
            <div className="camera-container-profile">

              <FormControl fullWidth>
                <Select
                  className="config-select"
                  value={selectedCamera?.deviceId || ""}
                  onChange={(e) => setSelectedCamera(e.target.value)}
                  sx={{
                    height: "45px",
                    marginTop: "10px",
                    marginBottom: "10px",
                    background: "#f5f7fa",
                    borderRadius: "12px",
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                >
                  {videoDevicesRef.current.map((d, i) => (
                    <MenuItem key={d.deviceId} value={d.deviceId}>
                      {d.label || `Camera ${i + 1}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <video className="camera-video-profile"
                ref={videoRef}
                autoPlay
                muted
                playsInline
                onLoadedMetadata={() => setIsVideoReady(true)}
                onPlaying={() => setIsVideoReady(true)}
              ></video>
            </div>
          </DialogContent>

          <DialogActions sx={{ marginTop: "8px" }}>
            <Button
              sx={{
                color: "#60D744",
                padding: "11px 22px",
                fontFamily: "Poppins",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                textTransform: "none",
              }}
              onClick={() => {
                onClose();
              }}
            >
              {t("cancel")}
            </Button>
            {isVideoReady ? (
              <Button
                sx={{
                  display: "flex",
                  padding: "11px 22px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "10px",
                  background: "#60D744",
                  borderRadius: "8px",
                  color: "#0C0C0C",
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "normal",
                  textTransform: "none",
                }}
                onClick={() => {
                  handleCapture();
                }}>
                <span className="material-symbols-outlined">
                  photo_camera
                </span>
                {t("take_photo")}
              </Button>) : (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="16.5%"
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </DialogActions>
        </>
      )}
    </>
  );
};

export default CameraCapture